import { gql, useQuery } from '@apollo/client'
import orderBy from 'lodash/orderBy'
import moment from 'moment'
import { useMemo } from 'react'

import { DATE_DATABASE_FORMAT } from 'pared/constants'

import { useVariables } from '../../../variables'

/**
 * QUERY_TREND_DATE_RANGES uses the new SQL function `list_trend_date_ranges` to efficiently
 * calculate date ranges for trend analysis. This is Step 1 of our two-step approach to
 * avoid timeout issues with the previous single large query.
 *
 * The SQL function returns business date information (year, quarter, month, week) along with
 * start/end dates for each time range, based on the grouping type (weeks, periods, quarters, years).
 */
const QUERY_TREND_DATE_RANGES = gql`
  query ListTrendDateRanges(
    $iEndDate: Date!
    $iGroupBy: String!
    $iDateRangeNumber: Int!
  ) {
    listTrendDateRanges(
      iEndDate: $iEndDate
      iGroupBy: $iGroupBy
      iDateRangeNumber: $iDateRangeNumber
    ) {
      nodes {
        businessYear
        businessQuarter
        businessMonth
        businessWeek
        businessWeekOfMonth
        startDate
        endDate
        dateIndex
      }
    }
  }
`

/**
 * QUERY_PMIX_DATA fetches item metric values for a specific date range.
 * This is Step 2 of our approach - we use this query multiple times sequentially,
 * once for each date range returned by QUERY_TREND_DATE_RANGES.
 *
 * This replaces the previous slow `trendItemMetricValuesByDateRangeNumber` query
 * which was causing timeout issues due to processing all date ranges at once.
 */
const QUERY_PMIX_DATA = gql`
  query ListItemMetricValues(
    $iStartDate: Date!
    $iEndDate: Date!
    $iQueryType: String!
    $iFilter: JSON!
  ) {
    listItemMetricValues(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iQueryType: $iQueryType
      iFilter: $iFilter
    ) {
      nodes {
        itemId
        itemCode
        itemName
        displayParentCategoryCode
        displayParentCategoryName
        metricData
        metricSummaryData
      }
    }
  }
`

/**
 * useSequentialTrendUnifiedData Hook
 *
 * This hook fetches trend data for product mix (PMIX) analysis across multiple time periods.
 *
 * REFACTORING NOTES (to address timeout issues):
 * Previously, this hook used a single `trendItemMetricValuesByDateRangeNumber` query that
 * fetched all 4 date ranges at once, which was causing database timeout issues.
 *
 * NEW APPROACH:
 * 1. First, fetch date ranges using the efficient `list_trend_date_ranges` SQL function
 * 2. Then, sequentially query metric values for each date range using `listItemMetricValues`
 * 3. Sequential queries prevent database blocking (each query waits for the previous to complete)
 * 4. Finally, combine all results to maintain the same output format as before
 *
 * @param options - Query options including variables (iEndDate, iQueryType, iFilter)
 * @returns Object with data (source items and summary) and loading state
 */
const useSequentialTrendUnifiedData = (options: any) => {
  const { variables } = useVariables()
  const dateType = variables.date?.value.type

  // Determine the grouping type based on the selected date type
  const iGroupBy = ((dateType?: string) => {
    switch (dateType) {
      case 'week':
      case 'this_week':
        return 'last_x_weeks'

      case 'period':
        return 'last_x_periods'

      case 'quarter':
        return 'last_x_quarters'

      case 'year':
        return 'last_x_years'

      default:
        return 'last_x_days'
    }
  })(dateType)

  const momentEndDate = moment.utc(
    options.variables.iEndDate,
    DATE_DATABASE_FORMAT,
    true,
  )

  /**
   * STEP 1: Fetch the date ranges
   * This query uses the new SQL function to efficiently calculate the 4 date ranges
   * we need for trend analysis (e.g., last 4 weeks, last 4 periods, etc.)
   */
  const { data: dateRangesData, loading: dateRangesLoading } = useQuery(
    QUERY_TREND_DATE_RANGES,
    {
      variables: {
        iEndDate: options.variables.iEndDate,
        iGroupBy,
        iDateRangeNumber: 4, // We want 4 historical date ranges for trend comparison
      },
      skip: !options.variables.iEndDate,
    },
  )

  // Extract and memoize the date ranges from the response
  const dateRanges = useMemo(() => {
    if (!dateRangesData?.listTrendDateRanges?.nodes) return []
    return dateRangesData.listTrendDateRanges.nodes
  }, [dateRangesData])

  /**
   * STEP 2: Sequential queries for each date range
   * We query each date range one at a time to avoid database blocking.
   * Each query waits for the previous one to complete before starting.
   */

  // Query data for the first date range (most recent period)
  const { data: range1Data, loading: range1Loading } = useQuery(
    QUERY_PMIX_DATA,
    {
      variables: {
        ...options.variables,
        iStartDate: dateRanges[0]?.startDate,
        iEndDate: dateRanges[0]?.endDate,
      },
      skip: !dateRanges[0] || dateRangesLoading,
    },
  )

  // Query data for the second date range
  // IMPORTANT: Only starts after the first query completes (waits for range1Data to exist)
  const { data: range2Data, loading: range2Loading } = useQuery(
    QUERY_PMIX_DATA,
    {
      variables: {
        ...options.variables,
        iStartDate: dateRanges[1]?.startDate,
        iEndDate: dateRanges[1]?.endDate,
      },
      skip: !dateRanges[1] || dateRangesLoading || !range1Data,
    },
  )

  // Query data for the third date range
  // IMPORTANT: Only starts after the second query completes (waits for range2Data to exist)
  const { data: range3Data, loading: range3Loading } = useQuery(
    QUERY_PMIX_DATA,
    {
      variables: {
        ...options.variables,
        iStartDate: dateRanges[2]?.startDate,
        iEndDate: dateRanges[2]?.endDate,
      },
      skip: !dateRanges[2] || dateRangesLoading || !range2Data,
    },
  )

  // Query data for the fourth date range (oldest period)
  // IMPORTANT: Only starts after the third query completes (waits for range3Data to exist)
  const { data: range4Data, loading: range4Loading } = useQuery(
    QUERY_PMIX_DATA,
    {
      variables: {
        ...options.variables,
        iStartDate: dateRanges[3]?.startDate,
        iEndDate: dateRanges[3]?.endDate,
      },
      skip: !dateRanges[3] || dateRangesLoading || !range3Data,
    },
  )

  // Combine all loading states - the entire hook is loading if any query is loading
  const loading =
    dateRangesLoading ||
    range1Loading ||
    range2Loading ||
    range3Loading ||
    range4Loading

  /**
   * STEP 3: Process and combine data from all date ranges
   * This maintains the same data structure as the original implementation,
   * ensuring backward compatibility while using the new sequential query approach.
   */
  const processedData = useMemo(() => {
    const currentWeekEndDate = momentEndDate.format(DATE_DATABASE_FORMAT)

    // Combine all range data with their corresponding date info
    // Each item contains the query result and its associated date range metadata
    const allRangeData = [
      { data: range1Data, dateInfo: dateRanges[0] },
      { data: range2Data, dateInfo: dateRanges[1] },
      { data: range3Data, dateInfo: dateRanges[2] },
      { data: range4Data, dateInfo: dateRanges[3] },
    ].filter((item) => item.data && item.dateInfo)

    if (allRangeData.length === 0) return null

    const idToSourceDataMap: { [id: number]: any } = {}
    const otherCategoryData: { [key: string]: number } = {}
    const summary: { [key: string]: number } = {}

    /**
     * Process data for each date range
     * For each date range, we:
     * 1. Extract metric values for each item
     * 2. Convert metric units (multiply by 100 for DOLLAR and PERCENTAGE)
     * 3. Append week-specific suffix to metric keys (e.g., "totalSalesWeek1InPast")
     * 4. Separate "Other" category data for special handling
     * 5. Accumulate summary data across all items
     */
    allRangeData.forEach(({ data, dateInfo }) => {
      const rangeData = data?.listItemMetricValues?.nodes
      if (!rangeData) return

      // Create a week identifier based on the date index (1-4, representing weeks in the past)
      const weekStr = `Week${dateInfo.dateIndex}InPast`

      rangeData.forEach((itemData: any) => {
        let existingData: any = idToSourceDataMap[itemData.itemId]
        if (!existingData) {
          existingData = {
            itemId: itemData.itemId,
            itemName: itemData.itemName,
            itemCode: itemData.itemCode,
            categoryName: itemData.itemName,
            categoryCode: itemData.itemCode,
            displayParentCategoryName: itemData.displayParentCategoryName,
            displayParentCategoryCode: itemData.displayParentCategoryCode,
            currentWeekEndDate,
            // Add date range info
            businessYear: dateInfo.businessYear,
            businessQuarter: dateInfo.businessQuarter,
            businessMonth: dateInfo.businessMonth,
            businessWeek: dateInfo.businessWeek,
            businessWeekOfMonth: dateInfo.businessWeekOfMonth,
            startDate: dateInfo.startDate,
            endDate: dateInfo.endDate,
            dateIndex: dateInfo.dateIndex,
          }
        }

        // Hide `Other` category
        if (itemData.itemCode.includes('Other')) {
          for (const key in itemData.metricData) {
            if (itemData.metricData.hasOwnProperty(key)) {
              let newKey = key.replace(/_(.)/g, (_, char) => char.toUpperCase())
              newKey = `${newKey}${weekStr}`

              switch (itemData.metricData[key].unit) {
                case 'DOLLAR':
                case 'PERCENTAGE':
                  if (!otherCategoryData.hasOwnProperty(newKey)) {
                    otherCategoryData[newKey] =
                      itemData.metricData[key].value * 100
                  } else {
                    otherCategoryData[newKey] =
                      otherCategoryData[newKey] +
                      itemData.metricData[key].value * 100
                  }
                  break

                default:
                  if (!otherCategoryData.hasOwnProperty(newKey)) {
                    otherCategoryData[newKey] = itemData.metricData[key].value
                  } else {
                    otherCategoryData[newKey] =
                      otherCategoryData[newKey] + itemData.metricData[key].value
                  }
                  break
              }
            }
          }
        } else {
          for (const key in itemData.metricData) {
            if (itemData.metricData.hasOwnProperty(key)) {
              let newKey = key.replace(/_(.)/g, (_, char) => char.toUpperCase())
              newKey = `${newKey}${weekStr}`

              switch (itemData.metricData[key].unit) {
                case 'DOLLAR':
                case 'PERCENTAGE':
                  existingData[newKey] = itemData.metricData[key].value * 100
                  break

                default:
                  existingData[newKey] = itemData.metricData[key].value
                  break
              }
            }
          }

          // we won't show the data if only has location_total_sales
          const metrics = Object.keys(itemData.metricData)
          if (
            metrics.includes('quantity_sold') ||
            metrics.includes('total_sales')
          ) {
            idToSourceDataMap[itemData.itemId] = existingData
          }
        }

        // Process summary data
        if (itemData.metricSummaryData) {
          const metricSummaryData = itemData.metricSummaryData

          for (const key in metricSummaryData) {
            if (metricSummaryData.hasOwnProperty(key)) {
              let newKey = key.replace(/_(.)/g, (_, char) => char.toUpperCase())
              newKey = `${newKey}${weekStr}`

              switch (metricSummaryData[key].unit) {
                case 'DOLLAR':
                case 'PERCENTAGE':
                  summary[newKey] = metricSummaryData[key].value * 100
                  break

                default:
                  summary[newKey] = metricSummaryData[key].value
                  break
              }
            }
          }
        }
      })
    })

    /**
     * Calculate the final summary by removing "Other" category values
     * This ensures the summary only reflects the visible categories
     */
    const filteredSummary: { [key: string]: number } = {}

    for (const key in summary) {
      if (otherCategoryData.hasOwnProperty(key)) {
        // Subtract "Other" category values from the total
        filteredSummary[key] = summary[key] - otherCategoryData[key]
      } else {
        filteredSummary[key] = summary[key]
      }
    }

    // Return the processed data in the expected format
    return {
      // Sort items by category and item names for consistent display
      source: orderBy(Object.values(idToSourceDataMap), [
        'categoryName',
        'subcategoryName',
        'itemName',
      ]),
      // Summary row with totals across all visible categories
      summary: {
        ...filteredSummary,
        currentWeekEndDate,
        categoryName: 'TOTAL',
      },
    }
  }, [
    dateRanges,
    range1Data,
    range2Data,
    range3Data,
    range4Data,
    momentEndDate,
  ])

  // Generate uniqueKey based on itemIds from the first range data
  // This ensures the key stays stable during progressive column loading
  const uniqueKey = useMemo(() => {
    if (!range1Data?.listItemMetricValues?.nodes) {
      return undefined
    }

    // Extract and sort itemIds to create a stable key
    const itemIds = range1Data.listItemMetricValues.nodes
      .map((item: any) => item.itemId)
      .filter((id: any) => id != null)
      .sort((a: number, b: number) => a - b)
      .join(',')

    return itemIds || 'empty'
  }, [range1Data])

  return {
    data: processedData,
    uniqueKey,
    loading,
  }
}

export default useSequentialTrendUnifiedData
